import React, { useContext, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faChevronUp, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { ChangesContext } from '../../Contexts';
import { FrontendEntityChangeService } from '../../Services/FrontendEntityChangeService';

interface SaveButtonProps {
    type?: 'button' | 'submit' | 'reset';
    onClick?: () => void;
    disabled?: boolean;
    iconOnly?: boolean;
    text?: string;
    isLoading?: boolean;
    /**
     * The form's own save action (the same logic its submit handler runs).
     * Return `false` to signal validation failed / nothing was saved, so the sync is skipped.
     * Provide together with `websiteId` to show the "Save and sync all changes" option.
     */
    onSave?: () => void | boolean | Promise<void | boolean>;
    /** Website whose pending changes get synced by "Save and sync all changes". */
    websiteId?: number;
}

const SaveButton: React.FC<SaveButtonProps> = ({
    type = 'submit',
    onClick,
    disabled = false,
    iconOnly = false,
    text = 'Uložiť',
    isLoading = false,
    onSave,
    websiteId,
}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const { syncChanges } = FrontendEntityChangeService();
    const { setHasChanges } = useContext(ChangesContext);

    const canSaveAndSync = !!onSave && websiteId != null;
    const isDisabled = disabled || isLoading || isSyncing;

    useEffect(() => {
        if (!menuOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    const handleSaveAndSync = async () => {
        const form = toggleRef.current?.form;
        if (form && !form.checkValidity()) {
            form.reportValidity();
            return;
        }
        setMenuOpen(false);
        setIsSyncing(true);
        try {
            const result = await onSave!();
            if (result === false) return;
            await syncChanges(websiteId!);
            setHasChanges(false);
        } finally {
            setIsSyncing(false);
        }
    };

    const mainButton = (
        <button
            type={type}
            onClick={onClick}
            disabled={isDisabled}
            className={`st-btn st-btn--success${iconOnly ? ' st-btn--icon' : ''}${canSaveAndSync ? ' st-btn-split__main' : ''}`}
        >
            {isLoading ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faSave} />}
            {!iconOnly && <span>{isLoading ? 'Ukladám...' : text}</span>}
        </button>
    );

    if (!canSaveAndSync) return mainButton;

    return (
        <div className="st-btn-split" ref={wrapRef}>
            {mainButton}
            <button
                ref={toggleRef}
                type="button"
                disabled={isDisabled}
                onClick={() => setMenuOpen(v => !v)}
                className="st-btn st-btn--success st-btn-split__toggle"
                aria-haspopup="true"
                aria-expanded={menuOpen}
                title="Ďalšie možnosti uloženia"
            >
                <FontAwesomeIcon icon={faChevronUp} className="st-btn-split__toggle-icon" />
            </button>

            {menuOpen && (
                <div className="st-btn-split__menu">
                    <button type="button" className="st-btn-split__item" onClick={handleSaveAndSync}>
                        {isSyncing ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faRefresh} />}
                        {isSyncing ? 'Synchronizujem...' : 'Uložiť a synchronizovať všetky zmeny'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default SaveButton;
